import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API, ytThumbFallback } from "@/lib/api";
import ProjectCard, { isVertical, cardImage } from "@/components/ProjectCard";
import Lightbox from "@/components/Lightbox";
import { Play } from "lucide-react";

const prefersReduced = () =>
  typeof window !== "undefined" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/* ---------- Hero ---------- */
const Hero = ({ hero }) => {
  const fullPrompt = hero.prompt || "";
  const steps = hero.denoise_steps || 28;
  const reduced = prefersReduced();
  const [typed, setTyped] = useState(reduced ? fullPrompt : "");
  const [rendered, setRendered] = useState(reduced);
  const [step, setStep] = useState(reduced ? steps : 0);

  useEffect(() => {
    if (reduced) return;
    let i = 0;
    const typer = setInterval(() => {
      i += 1;
      setTyped(fullPrompt.slice(0, i));
      if (i >= fullPrompt.length) {
        clearInterval(typer);
        setRendered(true);
        let s = 0;
        const counter = setInterval(() => {
          s += 1;
          setStep(s);
          if (s >= steps) clearInterval(counter);
        }, 1600 / steps);
      }
    }, 26);
    return () => clearInterval(typer);
    // eslint-disable-next-line
  }, []);

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-screen flex flex-col justify-center pad-x pt-28 pb-20 overflow-hidden"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute -top-40 -right-40 w-[60vw] h-[60vw] rounded-full blur-[120px] opacity-50"
        style={{ background: "radial-gradient(circle, rgba(191,168,255,0.35), rgba(80,90,200,0.12) 45%, transparent 70%)" }}
      />

      <div className="relative max-w-[1400px] w-full mx-auto">
        <div
          data-testid="hero-typing-prompt"
          className="font-mono text-xs sm:text-sm text-lav/90 mb-7 sm:mb-9"
        >
          {typed}<span className="blink">▋</span>
        </div>

        <h1
          data-testid="hero-name-reveal"
          className={`denoise ${rendered ? "rendered" : ""} font-display font-extrabold uppercase leading-[0.9] tracking-tight`}
          style={{ fontSize: "clamp(48px,10.5vw,150px)" }}
        >
          <span className="block">{hero.name_line1}</span>
          <span className="block name-outline">{hero.name_line2}</span>
        </h1>

        <div className="mt-9 sm:mt-12 grid md:grid-cols-2 gap-8 items-end">
          <p className="font-body text-base sm:text-lg max-w-xl text-mute">
            <span className="text-txt font-semibold">{hero.sub_bold} </span>
            {hero.sub_text}
          </p>
          <div className="font-mono text-[11px] sm:text-xs text-mute md:text-right">
            denoise step {step}/{steps}
          </div>
        </div>

        <div className="mt-14 font-mono text-[11px] sm:text-xs text-mute">
          {hero.scroll_hint}
        </div>
      </div>
    </section>
  );
};

/* ---------- Marquee ---------- */
const Marquee = ({ items }) => {
  const row = (
    <span className="inline-flex items-center">
      {items.map((it, i) => (
        <span key={`${it}-${i}`} className="inline-flex items-center font-mono text-xs sm:text-sm uppercase tracking-wider text-mute">
          <span className="px-5">{it}</span>
          <span className="text-lav">·</span>
        </span>
      ))}
    </span>
  );
  return (
    <div data-testid="tech-marquee" className="border-y border-hairline py-4 overflow-hidden bg-surface/40">
      <div className="marquee-track">
        {row}{row}
      </div>
    </div>
  );
};

/* ---------- Showreel ---------- */
const thumbFallback = (p) => (e) => {
  if (p.youtube_id && !e.target.dataset.fallback) {
    e.target.dataset.fallback = "1";
    e.target.src = ytThumbFallback(p.youtube_id);
  }
};

// maxresdefault 404s still return a tiny grey placeholder image, so onError
// never fires — detect it by width on load and swap to hqdefault.
const thumbPlaceholderSwap = (p) => (e) => {
  if (p.youtube_id && !e.target.dataset.fallback && e.target.naturalWidth <= 120) {
    e.target.dataset.fallback = "1";
    e.target.src = ytThumbFallback(p.youtube_id);
  }
};

const Showreel = ({ section, project, onOpen }) => {
  if (!project) return null;
  const img = cardImage(project);
  const open = () => onOpen(project);
  return (
    <section id="showreel" data-testid="showreel-section" className="pad-x py-20 sm:py-28">
      <div className="max-w-[1400px] mx-auto">
        <p className="reveal font-mono text-xs uppercase tracking-widest text-lav mb-6">{section.label}</p>
        <div
          role="button"
          tabIndex={0}
          aria-label={`Play ${project.title}`}
          data-testid="showreel-band"
          onClick={open}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); open(); } }}
          className="reveal group relative w-full aspect-[21/9] overflow-hidden rounded-xl bg-surface border border-hairline cursor-pointer"
        >
          {img ? (
            <img
              src={img}
              alt={project.title}
              onError={thumbFallback(project)}
              onLoad={thumbPlaceholderSwap(project)}
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
            />
          ) : (
            <div className="absolute inset-0" style={{ background: "linear-gradient(135deg,#1b1b24,#13131a 60%,#0d0d12)" }} />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/25 to-black/10" aria-hidden />
          <div className="absolute inset-0 grid place-items-center">
            <span
              data-testid="showreel-play-btn"
              className="inline-flex items-center gap-3 rounded-full bg-lav text-ink font-mono text-xs sm:text-sm uppercase tracking-wide px-6 py-3 sm:px-8 sm:py-4 transition-transform duration-300 group-hover:scale-105"
            >
              <Play size={15} className="fill-ink" /> Watch showreel
            </span>
          </div>
          <div className="absolute bottom-4 left-5 right-5 flex items-center justify-between font-mono text-[10px] sm:text-[11px] uppercase tracking-wide">
            <span className="text-lav">{project.model}</span>
            <span className="text-txt/70">{project.title} · {project.format}</span>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ---------- Award-Winning Films ---------- */
const AwardCard = ({ film, index, onOpen }) => {
  const img = cardImage(film);
  return (
    <article className="reveal group flex flex-col" style={{ transitionDelay: `${index * 90}ms` }} data-testid="award-card">
      <div
        role="button"
        tabIndex={0}
        aria-label={`Play ${film.title}`}
        onClick={() => onOpen(film)}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen(film); } }}
        className="relative overflow-hidden rounded-xl bg-surface border border-hairline cursor-pointer aspect-video"
      >
        {img ? (
          <img
            src={img}
            alt={film.title}
            loading="lazy"
            onError={thumbFallback(film)}
            onLoad={thumbPlaceholderSwap(film)}
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
          />
        ) : (
          <div className="absolute inset-0" style={{ background: "linear-gradient(135deg,#1b1b24,#13131a 60%,#0d0d12)" }} />
        )}
        <span className="absolute top-3 right-3 z-20 flex items-center gap-1.5 rounded-full bg-black/70 backdrop-blur px-2.5 py-1 font-mono text-[11px] text-txt">
          <Play size={11} className="fill-lav text-lav" /> watch with sound
        </span>
        <div className="prompt-drawer absolute inset-x-0 bottom-0 z-10 p-4 sm:p-5 bg-black/55 backdrop-blur-md border-t border-lav/40">
          <p className="font-mono text-[11px] sm:text-xs leading-relaxed text-txt/90">
            <span className="text-lav">&gt; </span>{film.prompt}
          </p>
        </div>
      </div>

      {film.award && (
        <p data-testid="award-line" className="mt-4 font-mono text-xs sm:text-sm leading-relaxed text-lav">{film.award}</p>
      )}
      <div className="mt-2 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="font-display text-xl sm:text-2xl leading-snug" data-testid="award-title">{film.title}</h3>
          <p className="font-body text-sm text-mute mt-1">{film.descriptor}</p>
        </div>
        <span className="shrink-0 mt-1 font-mono text-[10px] uppercase tracking-wide text-mute border border-hairline rounded-full px-2 py-1">Film</span>
      </div>
      <div className="mt-2.5 flex items-center justify-between font-mono text-[10px] sm:text-[11px] uppercase tracking-wide">
        <span className="text-lav truncate pr-2">{film.model}</span>
        <span className="text-mute whitespace-nowrap">{film.format}</span>
      </div>
    </article>
  );
};

const AwardFilms = ({ section, films, onOpen }) => {
  if (!films.length) return null;
  return (
    <section id="awards" data-testid="awards-section" className="pad-x py-24 sm:py-32 border-t border-hairline">
      <div className="max-w-[1400px] mx-auto">
        <div className="reveal mb-12">
          <p className="font-mono text-xs uppercase tracking-widest text-lav mb-3">{section.eyebrow || "recognition"}</p>
          <h2 className="font-display font-bold text-4xl sm:text-5xl">{section.label}</h2>
        </div>
        <div className="grid md:grid-cols-2 gap-6 lg:gap-10">
          {films.map((f, i) => (
            <AwardCard key={f.id} film={f} index={i} onOpen={onOpen} />
          ))}
        </div>
      </div>
    </section>
  );
};

/* ---------- Work ---------- */
const Work = ({ work, categories, projects, onOpen }) => {
  const [active, setActive] = useState("all");
  const catLabel = useMemo(() => {
    const m = {}; categories.forEach((c) => (m[c.slug] = c.label)); return m;
  }, [categories]);

  const enriched = projects.map((p) => ({ ...p, categoryLabel: catLabel[p.category] || p.category }));
  const norm = (s) => (s || "").trim().toLowerCase();
  const matches = (p) => active === "all" || norm(p.category) === norm(active);
  // Keep EVERY card in the DOM; filtering only toggles visibility.
  const landscape = enriched.filter((p) => !isVertical(p));
  const reels = enriched.filter((p) => isVertical(p));
  const visibleCount = enriched.filter(matches).length;
  const visibleLandscape = landscape.filter(matches).length;
  const visibleReels = reels.filter(matches).length;

  return (
    <section id="work" data-testid="work-section" className="pad-x py-24 sm:py-32">
      <div className="max-w-[1400px] mx-auto">
        <div className="reveal mb-10">
          <p className="font-mono text-xs uppercase tracking-widest text-lav mb-3">{work.eyebrow}</p>
          <h2 className="font-display font-bold text-4xl sm:text-5xl">{work.heading}</h2>
        </div>

        <div data-testid="work-filters" className="reveal flex flex-wrap gap-2.5 mb-12" role="tablist" aria-label="Project filters">
          {[{ slug: "all", label: "All" }, ...categories].map((c) => (
            <button
              key={c.slug}
              role="tab"
              aria-selected={active === c.slug}
              data-testid={`filter-${c.slug}`}
              onClick={() => setActive(c.slug)}
              className={`font-mono text-xs uppercase tracking-wide rounded-full px-4 py-2 border transition-colors duration-300 ${
                active === c.slug
                  ? "bg-lav text-ink border-lav"
                  : "border-hairline text-mute hover:text-txt hover:border-txt/40"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>

        {landscape.length > 0 && (
          <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 ${visibleLandscape === 0 ? "hidden" : ""}`}>
            {landscape.map((p, i) => (
              <ProjectCard key={p.id} project={p} index={i} onOpen={onOpen} visible={matches(p)} />
            ))}
          </div>
        )}

        {reels.length > 0 && (
          <div className={`grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-6 ${visibleReels === 0 ? "hidden" : (visibleLandscape > 0 ? "mt-6" : "")}`}>
            {reels.map((p, i) => (
              <ProjectCard key={p.id} project={p} index={i} onOpen={onOpen} visible={matches(p)} />
            ))}
          </div>
        )}

        {visibleCount === 0 && (
          <p data-testid="no-projects" className="font-mono text-sm text-mute">No projects in this category yet</p>
        )}

        <p className="reveal mt-14 font-mono text-xs text-mute">
          <a href="https://www.instagram.com/itshemanth.kumar" target="_blank" rel="noreferrer" className="hover:text-lav transition-colors">
            {work.footer_line}
          </a>
        </p>
      </div>
    </section>
  );
};

/* ---------- Process ---------- */
const Process = ({ process }) => (
  <section id="process" data-testid="process-section" className="pad-x py-24 sm:py-32 border-t border-hairline">
    <div className="max-w-[1400px] mx-auto">
      <div className="reveal mb-12">
        <p className="font-mono text-xs uppercase tracking-widest text-lav mb-3">{process.eyebrow}</p>
        <h2 className="font-display font-bold text-4xl sm:text-5xl">{process.heading}</h2>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6">
        {(process.steps || []).map((s, i) => (
          <div key={`${s.number}-${s.title}`} className="reveal bg-surface border border-hairline rounded-xl p-6" style={{ transitionDelay: `${i * 80}ms` }}>
            <div className="font-mono text-xs text-lav uppercase tracking-wide mb-6">{s.number} / {s.label}</div>
            <h3 className="font-display text-lg mb-3">{s.title}</h3>
            <p className="font-body text-sm text-mute leading-relaxed">{s.body}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

/* ---------- About ---------- */
const renderLede = (text) =>
  text.split("*").map((part, i) =>
    i % 2 === 1 ? <em key={i} className="italic text-lav">{part}</em> : <span key={i}>{part}</span>
  );

const About = ({ about }) => (
  <section id="about" data-testid="about-section" className="pad-x py-24 sm:py-32 border-t border-hairline">
    <div className="max-w-[1400px] mx-auto">
      <p className="reveal font-mono text-xs uppercase tracking-widest text-lav mb-10">{about.eyebrow}</p>
      <div className="grid md:grid-cols-2 gap-12 lg:gap-20">
        <div className="reveal">
          <p className="font-display text-2xl sm:text-3xl lg:text-4xl leading-snug">
            {renderLede(about.lede || "")}
          </p>
          <p className="font-body text-mute mt-8 leading-relaxed max-w-lg">{about.body}</p>

          <div className="mt-12">
            <p className="font-mono text-xs uppercase tracking-widest text-mute mb-4">{about.brands_label}</p>
            <p className="font-display text-lg sm:text-xl leading-relaxed">
              {(about.brands || []).map((b, i) => (
                <span key={b}>
                  {b}
                  {i < about.brands.length - 1 && <span className="text-mute px-2">·</span>}
                </span>
              ))}
            </p>
          </div>
        </div>

        <div className="reveal">
          {(about.capabilities || []).map((c) => (
            <div key={`${c.name}-${c.tools}`} className="flex items-baseline justify-between gap-4 py-4 border-b border-hairline">
              <span className="font-display text-lg">{c.name}</span>
              <span className="font-mono text-xs text-mute text-right">{c.tools}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

/* ---------- Contact ---------- */
const Contact = ({ contact }) => (
  <section id="contact" data-testid="contact-section" className="pad-x py-28 sm:py-36 border-t border-hairline text-center">
    <div className="max-w-[1400px] mx-auto flex flex-col items-center">
      <p className="reveal font-mono text-xs uppercase tracking-widest text-lav mb-6">{contact.eyebrow}</p>
      <h2 className="reveal font-display font-bold text-3xl sm:text-5xl mb-10">{contact.heading}</h2>
      <a
        href={`mailto:${contact.email}`}
        data-testid="contact-mailto"
        className="reveal font-display font-bold break-all text-4xl sm:text-6xl lg:text-7xl hover:text-lav transition-colors duration-300"
      >
        {contact.email}
      </a>
      <div className="reveal mt-12 flex flex-wrap justify-center gap-x-8 gap-y-3 font-mono text-xs text-mute">
        <span>instagram → <a className="hover:text-lav transition-colors" href="https://www.instagram.com/itshemanth.kumar" target="_blank" rel="noreferrer">{contact.instagram_handle}</a></span>
        <span>based in → {contact.location}</span>
        <span>open to → {contact.open_to}</span>
      </div>
    </div>
  </section>
);

/* ---------- Nav ---------- */
const Nav = ({ nav }) => {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <nav
      data-testid="main-navigation"
      className={`fixed top-0 inset-x-0 z-50 pad-x transition-colors duration-500 ${scrolled ? "bg-ink/85 backdrop-blur-md border-b border-hairline" : "bg-gradient-to-b from-ink/90 to-transparent"}`}
    >
      <div className="max-w-[1400px] mx-auto flex items-center justify-between h-16">
        <a href="#top" data-testid="nav-logo" className="font-mono text-sm text-txt flex items-center gap-1">
          {nav.logo}<span className="blink text-lav">▋</span>
        </a>
        <div className="flex items-center gap-5 sm:gap-7 font-mono text-xs uppercase tracking-wide">
          {(nav.links || []).map((l) => (
            <a key={l.href} href={l.href} className="hidden sm:inline text-mute hover:text-txt transition-colors">{l.label}</a>
          ))}
          <a href={nav.instagram_url} target="_blank" rel="noreferrer" className="text-lav hover:opacity-80 transition-opacity">
            {nav.instagram_label}
          </a>
        </div>
      </div>
    </nav>
  );
};

/* ---------- Footer ---------- */
const Footer = ({ footer }) => (
  <footer className="pad-x py-10 border-t border-hairline">
    <div className="max-w-[1400px] mx-auto flex flex-col sm:flex-row sm:items-center justify-between gap-3 font-mono text-[11px] uppercase tracking-wide text-mute">
      <span>{footer.left}</span>
      <Link to="/login" className="hover:text-lav transition-colors" data-testid="owner-login-link">{footer.right}</Link>
    </div>
  </footer>
);

/* ---------- Page ---------- */
export default function Portfolio() {
  const [data, setData] = useState(null);
  const [active, setActive] = useState(null);

  useEffect(() => {
    axios.get(`${API}/content`).then((r) => setData(r.data)).catch(() => {});
  }, []);

  // scroll reveal
  useEffect(() => {
    if (!data) return;
    if (prefersReduced()) {
      document.querySelectorAll(".reveal").forEach((el) => el.classList.add("in-view"));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.add("in-view"); io.unobserve(e.target); } }),
      { threshold: 0.12 }
    );
    document.querySelectorAll(".reveal").forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [data]);

  if (!data || !data.site) {
    return <div className="min-h-screen grid place-items-center bg-ink text-mute font-mono text-sm">rendering…</div>;
  }

  const { site, categories, projects } = data;
  const isSection = (c) => (c.section_type || "filter") !== "filter";
  const sections = categories.filter(isSection);
  const filterCats = categories.filter((c) => !isSection(c));
  const sectionSlugs = new Set(sections.map((c) => c.slug));
  const workProjects = projects.filter((p) => !sectionSlugs.has(p.category));

  return (
    <div className="bg-ink text-txt">
      <div className="grain" aria-hidden />
      <Nav nav={site.nav} />
      <Hero hero={site.hero} />
      <Marquee items={site.marquee || []} />
      {sections.map((sec) =>
        sec.section_type === "showreel" ? (
          <Showreel key={sec.id} section={sec} project={projects.find((p) => p.category === sec.slug)} onOpen={setActive} />
        ) : (
          <AwardFilms key={sec.id} section={sec} films={projects.filter((p) => p.category === sec.slug)} onOpen={setActive} />
        )
      )}
      <Work work={site.work} categories={filterCats} projects={workProjects} onOpen={setActive} />
      <Process process={site.process} />
      <About about={site.about} />
      <Contact contact={site.contact} />
      <Footer footer={site.footer} />
      {active && <Lightbox project={active} onClose={() => setActive(null)} />}
    </div>
  );
}
